/**
 * Localisation analytics (REQ-23) and fallback monitoring (REQ-10).
 *
 * What is sent is deliberately small: the EFFECTIVE locale and how it was chosen. Never the
 * browser's full language list, never a region, never anything that reads as nationality.
 * Every event goes through `trackEvent`, so nothing leaves the browser without analytics
 * consent. Retention and activation BY LANGUAGE are derived downstream from `app_locale`
 * on `language_resolved`; they need no extra event.
 */
import { trackEvent } from "../lib/analytics";
import type { AppLocale } from "@shared/i18n/localeConfig";
import type { LocaleSource } from "@shared/i18n/localePreference";
import { I18N_DEV_TOOLS } from "./i18nEnv";
import type { FallbackEvent } from "./translator";

/** At most this many distinct fallback events per page load reach analytics. */
export const MAX_FALLBACK_EVENTS_PER_SESSION = 25;

const reported = new Set<string>();
let resolvedReported = false;

export function reportFallback(event: FallbackEvent): void {
  const id = `${event.locale}|${event.key}|${event.reason}`;
  if (reported.has(id)) return;
  reported.add(id);
  if (I18N_DEV_TOOLS) {
    console.warn(`[i18n] ${event.reason}: "${event.key}" (${event.locale})`);
  }
  if (reported.size <= MAX_FALLBACK_EVENTS_PER_SESSION) {
    trackEvent("i18n_fallback", { i18n_key: event.key, app_locale: event.locale, reason: event.reason });
  }
}

/** Once per page load: the language the app settled on, and whether the player chose it. */
export function reportLocaleResolved(locale: AppLocale, source: LocaleSource): void {
  if (resolvedReported) return;
  resolvedReported = true;
  trackEvent("language_resolved", {
    app_locale: locale,
    locale_source: source,
    locale_selection: source === "manual" || source === "url" ? "manual" : "automatic",
  });
}

export function reportLocaleChanged(from: AppLocale, to: AppLocale, selection: "manual" | "automatic"): void {
  trackEvent("language_changed", { from_locale: from, app_locale: to, locale_selection: selection });
}

/** Test seam: forget what this page load already reported. */
export function resetI18nAnalyticsForTests(): void {
  reported.clear();
  resolvedReported = false;
}
